import { notifications } from "@marketplace/ui/notifications";
import { addMinutes } from "date-fns";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AUTH_SESSION_KEYS } from "@/constants/auth-session-keys";
import {
  loginOtpEmailAtom,
  loginOtpExpirationTimeAtom
} from "../atoms/login-atom";
import { OtpService } from "../services/otp-service";
import { usePostSendOtpMutation } from "./mutations/use-post-send-otp-mutation";

export const useSendOtp = () => {
  const { t } = useTranslation("auth");
  const [otpSent, setOtpSent] = useState(false);
  const { mutateAsync: sendOtp } = usePostSendOtpMutation();
  const email = useAtomValue(loginOtpEmailAtom);
  const [expiredAt, setExpiredAt] = useAtom(loginOtpExpirationTimeAtom);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!expiredAt || expiredAt === 0) return;

    setTimeLeft(expiredAt - Math.floor(Date.now() / 1000));
  }, [expiredAt]);

  useEffect(() => {
    if (!expiredAt || expiredAt === 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [expiredAt]);

  const canSendOtp = useMemo(() => {
    if (timeLeft > 0) return false;

    if (timeLeft <= 0 && !otpSent) return true;

    if (otpSent) return false;

    return true;
  }, [timeLeft, otpSent]);

  const handleSendOtp = async () => {
    try {
      const existingToken = sessionStorage.getItem(AUTH_SESSION_KEYS.OTP_TOKEN);
      if (!email || !existingToken) return;

      const expirationTime = Math.floor(
        addMinutes(new Date(), 3).getTime() / 1000
      );
      const newToken = await OtpService.getInstance().encrypt(
        email,
        expirationTime
      );

      const res = await sendOtp({ email });

      if (res) {
        setOtpSent(true);
        setExpiredAt(expirationTime);
        sessionStorage.setItem(AUTH_SESSION_KEYS.OTP_TOKEN, newToken);
        notifications.show({
          message: t("verifyOtp.otpHasBeenSent", {
            email: email ?? ""
          })
        });
      }
    } catch {
      notifications.show({
        variant: "error",
        message: t("verifyOtp.failedToSendOtp")
      });
    }
  };

  return {
    handleSendOtp,
    canSendOtp,
    timeLeft
  };
};
